// Story 8.10 (ADO-2019), DV-8/DV-38/Q10: "HHT history": the accepted orders, newest first, in a
// collapsible card (the 8.10 Figma exports, "History expanded"). Each row is the date, "Added
// by {name}" under it (left out when the name could not be read), a lines-and-cases pill (what
// the accept added, as the after-accept screen said) and an "N attention" (amber) or "Clean"
// (green) pill. The header pill counts every accepted order; the list holds the latest
// `HHT_HISTORY_LIMIT`, and says so under the rows when there are more.
import { useId, useState } from 'react';
import { Icon } from '../../design-system';
import { HHT_HISTORY_LIMIT, type HhtHistoryOrder } from '../../api/hht-orders.client';
import { formatDateTimeWithYear } from '../../shared/datetime';
import { linesAndCases } from './hhtFormat';
import { HhtPill } from './HhtPill';
import styles from './HhtOrders.module.css';

export function HhtHistory({
  history,
  total,
}: {
  readonly history: readonly HhtHistoryOrder[];
  /** Every accepted order of the Customer (the list holds at most the latest 20). */
  readonly total: number;
}) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  const count = Math.max(total, history.length);
  const orders = count === 1 ? '1 order' : `${count} orders`;

  return (
    <section className={styles.panel} aria-label="HHT history">
      <button
        type="button"
        className={styles.historyToggle}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        <span>HHT history</span>
        <HhtPill tone="outline">{orders}</HhtPill>
        <span className={styles.historyChevron} aria-hidden>
          <Icon name={open ? 'caretUp' : 'caretDown'} size={16} />
        </span>
      </button>
      {open ? (
        <div id={listId}>
          <ul className={styles.historyList}>
            {history.map((order) => (
              <li key={order.hhtOrderId} className={styles.historyRow}>
                <span className={styles.historyWhen}>
                  {/* The date the order was added to the carts, in the viewer's zone (DV-38). */}
                  <time className={styles.historyDate} dateTime={order.acceptedAt}>
                    {formatDateTimeWithYear(order.acceptedAt)}
                  </time>
                  {order.acceptedByName !== null ? (
                    <span className={styles.historyBy}>Added by {order.acceptedByName}</span>
                  ) : null}
                </span>
                <span className={styles.historyPills}>
                  <HhtPill tone="info">{linesAndCases(order.lineCount, order.caseCount)}</HhtPill>
                  {order.attentionLineCount > 0 ? (
                    <HhtPill tone="warning">{order.attentionLineCount} attention</HhtPill>
                  ) : (
                    <HhtPill tone="success">Clean</HhtPill>
                  )}
                </span>
              </li>
            ))}
          </ul>
          {count > HHT_HISTORY_LIMIT ? (
            <p className={`${styles.subtitle} ${styles.historyLimit}`}>
              {`Showing the latest ${HHT_HISTORY_LIMIT} orders.`}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
